import React, { useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Download } from "lucide-react";

import { GroupData } from "../../../../../types/GroupData";
import JudgingSheetTemplate from "./JudgingSheetTemplate";
import JudgingSheetTemplateShortForm from "./JudgingSheetTemplateShortForm";

import musicalityImg from "../../../../../assets/images/judgingSheet/musicality_Judging.png";
import performanceImg from "../../../../../assets/images/judgingSheet/performance_Judging.png";
import singingImg from "../../../../../assets/images/judgingSheet/singing_Judging.png";
import musicalityShortImg from "../../../../../assets/images/judgingSheet/Musicality_Judging_short_Form.png";
import performanceShortImg from "../../../../../assets/images/judgingSheet/Performance_Judging_short_Form.png";
import singingShortImg from "../../../../../assets/images/judgingSheet/Singing_Judging_short_Form.png";

export interface JudgePanel {
  MUS: string;
  PER: string;
  SNG: string;
}

// ZIPにまとめる1クラス分のグループ一覧。
// 複数クラスをまとめてダウンロードする場合は、クラスごとに
// entryClassLabel・dateStr（このクラスの開催日）を分けて持つ。
export interface JudgingSheetSectionEntries {
  entryClassLabel: string;
  dateStr: string;
  groups: GroupData[];
}

interface Props {
  // ダウンロード対象のクラス一覧（1クラスのみの場合も配列で渡す）
  sections: JudgingSheetSectionEntries[];
  judgePanel: JudgePanel;
  // ファイル名の先頭部分（例: "2026_QuartetContest_Final"）
  fileNamePrefix: string;
  // ボタンのツールチップ（hover時に表示するテキスト）
  buttonTitle: string;
  // true: short formのテンプレート画像を使用する
  useShortForm?: boolean;
}

// 曲1・曲2の2曲分作成する
const SONG_NUMBERS = [1, 2];

// PDFの物理ページサイズ（USレター：8.5×11インチ）。
// テンプレート側のキャプチャ解像度(1836x2376px)はちょうど216dpiのレターサイズなので、
// 実寸はここで固定する。
const PAGE_WIDTH_IN = 8.5;
const PAGE_HEIGHT_IN = 11;

const waitForPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

// 画像を読み込む（ブラウザキャッシュを温める役割と、末尾に追加する
// 「画像だけのページ」用にHTMLImageElementを取得する役割を兼ねる）
const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(img);
    img.src = url;
  });

// PDFの末尾に追加する「HTMLの記載がない、画像だけのページ」用にJPEGのdata URLへ変換する
const imageToJpegDataUrl = (img: HTMLImageElement): string => {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
  }
  return canvas.toDataURL("image/jpeg", 0.92);
};

// PDFの末尾に追加する、画像だけのページの枚数（short form版は1枚、通常版は2枚）
const BLANK_IMAGE_PAGE_COUNT_SHORT_FORM = 1;
const BLANK_IMAGE_PAGE_COUNT_REGULAR = 2;

const sanitizeFileName = (value: string) =>
  value.replace(/[\\/:*?"<>|]/g, "").trim();

interface RenderTarget {
  image: string;
  entryClassLabel: string;
  dateStr: string;
  judgeName: string;
  judgeNumber: number;
  // 通常版（1ページ=1グループ）
  songLabel?: string;
  groupName?: string;
  entryNumber?: number | string;
  // short form版（1ページ=2グループ、上下半分ずつ）
  topEntryNumber?: number | string;
  topGroupName?: string;
  bottomEntryNumber?: number | string;
  bottomGroupName?: string;
}

const JudgingSheetDownloadButton: React.FC<Props> = ({
  sections,
  judgePanel,
  fileNamePrefix,
  buttonTitle,
  useShortForm = false,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [current, setCurrent] = useState<RenderTarget | null>(null);

  const captureRef = useRef<HTMLDivElement>(null);

  // カテゴリーごとに担当する審査員（基本情報のJudge Panelから自動で割り当て）
  // judgeNumber はPDF上に "Judge #1" のように印字される、Judge Panel内の並び順
  const categories = [
    {
      key: "Musicality",
      image: useShortForm ? musicalityShortImg : musicalityImg,
      judgeName: judgePanel.MUS,
      judgeNumber: 1,
    },
    {
      key: "Performance",
      image: useShortForm ? performanceShortImg : performanceImg,
      judgeName: judgePanel.PER,
      judgeNumber: 2,
    },
    {
      key: "Singing",
      image: useShortForm ? singingShortImg : singingImg,
      judgeName: judgePanel.SNG,
      judgeNumber: 3,
    },
  ];

  const isJudgePanelReady =
    !!judgePanel.MUS && !!judgePanel.PER && !!judgePanel.SNG;

  const totalGroups = sections.reduce(
    (sum, section) => sum + section.groups.length,
    0,
  );

  // short form版は曲（SONG 1/SONG 2）ごとの分割がなく、また1ページに2グループ
  // （上半分・下半分）を印字するため、クラスごとにグループを2件ずつのペアに分ける。
  // 件数が奇数のときは最後のページは1件のみ（下半分は空）になる。
  const getShortFormPagesForSection = (groups: GroupData[]) => {
    const pages: { top: GroupData; bottom?: GroupData }[] = [];
    for (let i = 0; i < groups.length; i += 2) {
      pages.push({ top: groups[i], bottom: groups[i + 1] });
    }
    return pages;
  };

  const handleDownload = async () => {
    if (isGenerating) return;

    if (totalGroups === 0) {
      window.alert("対象のエントリーがありません。");
      return;
    }

    if (!isJudgePanelReady) {
      window.alert(
        "「基本情報」でこの年のJudge Panel（MUS・PER・SNG）をすべて設定してください。",
      );
      return;
    }

    setIsGenerating(true);

    // 末尾に追加する「画像だけのページ」の枚数（カテゴリーごとに追加する）
    const blankImagePageCount = useShortForm
      ? BLANK_IMAGE_PAGE_COUNT_SHORT_FORM
      : BLANK_IMAGE_PAGE_COUNT_REGULAR;

    // short form版は「クラスごとに2グループ1ページ」、通常版は「1グループ×曲数」でページ数を数える
    const pageCountPerCategory =
      (useShortForm
        ? sections.reduce(
            (sum, section) =>
              sum + getShortFormPagesForSection(section.groups).length,
            0,
          )
        : totalGroups * SONG_NUMBERS.length) + blankImagePageCount;

    const total = pageCountPerCategory * categories.length;
    setProgress({ done: 0, total });

    try {
      const loadedImages = await Promise.all(
        categories.map((category) => loadImage(category.image)),
      );

      const zip = new JSZip();
      let done = 0;

      // カテゴリー（Musicality/Performance/Singing）ごとに1つのPDFを作成し、
      // 対象クラス・グループ・曲のページをすべて1つのPDFにまとめる。
      // 最後に3つのPDFを1つのzipにまとめてダウンロードする。
      for (const [categoryIndex, category] of categories.entries()) {
        const doc = new jsPDF({
          unit: "in",
          format: [PAGE_WIDTH_IN, PAGE_HEIGHT_IN],
          orientation: "portrait",
          compress: true,
        });

        let isFirstPage = true;

        const capturePage = async () => {
          await waitForPaint();

          if (captureRef.current) {
            const canvas = await html2canvas(captureRef.current, {
              scale: 1,
              useCORS: true,
              backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/jpeg", 0.92);

            if (!isFirstPage) {
              doc.addPage([PAGE_WIDTH_IN, PAGE_HEIGHT_IN], "portrait");
            }

            doc.addImage(imgData, "JPEG", 0, 0, PAGE_WIDTH_IN, PAGE_HEIGHT_IN);
            isFirstPage = false;
          }

          done += 1;
          setProgress({ done, total });
        };

        for (const section of sections) {
          if (useShortForm) {
            // 2グループを1ページ（上半分・下半分）にまとめる
            for (const page of getShortFormPagesForSection(section.groups)) {
              setCurrent({
                image: category.image,
                entryClassLabel: section.entryClassLabel,
                dateStr: section.dateStr,
                judgeName: category.judgeName,
                judgeNumber: category.judgeNumber,
                topEntryNumber: page.top.entryNumber,
                topGroupName: page.top.groupName,
                bottomEntryNumber: page.bottom?.entryNumber,
                bottomGroupName: page.bottom?.groupName,
              });

              await capturePage();
            }
          } else {
            for (const group of section.groups) {
              for (const songNumber of SONG_NUMBERS) {
                setCurrent({
                  image: category.image,
                  entryClassLabel: section.entryClassLabel,
                  dateStr: section.dateStr,
                  songLabel: `SONG ${songNumber}`,
                  judgeName: category.judgeName,
                  judgeNumber: category.judgeNumber,
                  groupName: group.groupName,
                  entryNumber: group.entryNumber,
                });

                await capturePage();
              }
            }
          }
        }

        // 末尾に、HTMLの記載がない画像だけのページを追加する
        const blankImageDataUrl = imageToJpegDataUrl(
          loadedImages[categoryIndex],
        );
        for (let i = 0; i < blankImagePageCount; i += 1) {
          if (!isFirstPage) {
            doc.addPage([PAGE_WIDTH_IN, PAGE_HEIGHT_IN], "portrait");
          }
          doc.addImage(
            blankImageDataUrl,
            "JPEG",
            0,
            0,
            PAGE_WIDTH_IN,
            PAGE_HEIGHT_IN,
          );
          isFirstPage = false;

          done += 1;
          setProgress({ done, total });
        }

        const pdfBlob = doc.output("blob");
        zip.file(
          `${sanitizeFileName(fileNamePrefix)}_${category.key}.pdf`,
          pdfBlob,
        );
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${sanitizeFileName(fileNamePrefix)}_採点用紙.zip`);
    } catch (error) {
      console.error("採点用紙PDF生成エラー:", error);
      window.alert(
        "PDFの生成に失敗しました。時間をおいて再度お試しください。",
      );
    } finally {
      setCurrent(null);
      setProgress(null);
      setIsGenerating(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="download-btn pdf"
        onClick={handleDownload}
        disabled={isGenerating || totalGroups === 0}
        title={buttonTitle}
      >
        {isGenerating ? (
          progress ? `${progress.done}/${progress.total}` : "…"
        ) : (
          <Download size={16} />
        )}
      </button>

      {/* PDF化のためだけに画面外に描画する非表示テンプレート */}
      {current && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: -99999,
            zIndex: -1,
          }}
          aria-hidden="true"
        >
          {useShortForm ? (
            <JudgingSheetTemplateShortForm
              ref={captureRef}
              imageSrc={current.image}
              entryClassLabel={current.entryClassLabel}
              dateStr={current.dateStr}
              judgeName={current.judgeName}
              judgeNumber={current.judgeNumber}
              topEntryNumber={current.topEntryNumber}
              topGroupName={current.topGroupName ?? ""}
              bottomEntryNumber={current.bottomEntryNumber}
              bottomGroupName={current.bottomGroupName}
            />
          ) : (
            <JudgingSheetTemplate
              ref={captureRef}
              imageSrc={current.image}
              entryClassLabel={current.entryClassLabel}
              dateStr={current.dateStr}
              entryNumber={current.entryNumber}
              judgeName={current.judgeName}
              judgeNumber={current.judgeNumber}
              groupName={current.groupName ?? ""}
              songLabel={current.songLabel ?? ""}
            />
          )}
        </div>
      )}
    </>
  );
};

export default JudgingSheetDownloadButton;
